import React, {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { InjectedWindow, Wei } from '../../utils/types'
import SwapInputCurrency from '../swap/SwapInputCurrency'
import SwapInput from '../swap/SwapInput'
import { numericalPlaceholder } from '../../config'
import { MaxButton } from './MaxButton'
import { fromWei, toWei, numericTextAsWei } from '../../utils/static-web3'
import SwapInputPanel from '../swap/SwapInputPanel'
import styled from 'styled-components'
import { Web3Context } from '../../hooks/web3'
import SwapCurrencySelectorPanel from '../swap/SwapCurrencySelectorPanel'
import { Token } from '../../logic/token/token'
import SimpleBalance from './SimpleBalance'
import { TokenApi } from '../../api/web3/token-api'
import { SwapState } from '../swap/SwapState'
import { createEarnTabEvent } from 'user-events-segment'
declare const window: InjectedWindow

export interface Props {
  tokens: Token[]
  onTokenChange: (token: Token) => void
  amount: Wei | undefined
  onAmountChange: (amount: Wei | undefined) => string | undefined
  showBalance?: boolean
  showMax?: boolean
  enableToken?: (token: Token) => boolean
}

export const TokenSelector: FC<Props> = (props: Props) => {
  const f = () => true
  const showBalance = props.showBalance || true
  const showMax = props.showMax || true
  const enableToken = props.enableToken || f

  const { tokens, onTokenChange, onAmountChange } = props

  const inputRef = useRef<HTMLInputElement>(null)

  const ref = useRef()

  const [token, setToken] = useState<Token>(tokens[0])
  const [showSelector, setShowSelector] = useState(false)
  const [balance, setBalance] = useState<string | undefined>(undefined)
  const [loadingBalance, setLoadingBalance] = useState(false)
  const { web3, account } = useContext(Web3Context)

  useEffect(() => {
    reloadBalance()
  }, [token])

  function reloadBalance() {
    if (!web3 || !account) {
      // parent will ask to connect
      console.error('not connected')
      return
    }
    const tokenApi = new TokenApi(web3, account, token)
    setLoadingBalance(true)
    tokenApi
      .getERC20Balance()
      .then(balance => {
        setBalance(balance)
        setLoadingBalance(false)
      })
      .catch(console.error)
  }

  function selectCurrency(newToken: Token) {
    if (enableToken(newToken)) {
      setToken(newToken)
      setShowSelector(false)
      onTokenChange(newToken)
      if (inputRef.current && balance) {
        inputRef.current.value = ''
      }
      onAmountChange(undefined)
    }
  }

  const updateTokenQuantity = (e: ChangeEvent<HTMLInputElement>) => {
    if (isNaN(Number(e.target.value))) {
      if (inputRef.current) {
        inputRef.current.value =
          props.amount && token
            ? fromWei(props.amount, token.decimals).toString()
            : ''
      }
      return
    }
    const textValue = e.target.value
    try {
      let n: number | undefined = Number(textValue)
      let textAsWei = numericTextAsWei(textValue, token.decimals)
      if (isNaN(n)) {
        onAmountChange(undefined)
      } else {
        onAmountChange(textAsWei)
      }
    } catch (e) {
      onAmountChange(undefined)
    }
  }

  return (
    <SwapInputPanel>
      <div className={'justify-between'}>
        <SwapInput
          data-cy={'swaap-input'}
          ref={inputRef}
          onFocus={e => e.target.select()}
          onChange={updateTokenQuantity}
          placeholder={numericalPlaceholder}
          exceed={props.amount ? Number(props.amount) > Number(balance) : false}
        />
        <SwapInputCurrency
          onClick={() => {
            onAmountChange(undefined)
            setShowSelector(true)

            createEarnTabEvent(
              window.applicationContext.selectedPool
            ).opened_token_selector(
              window.applicationContext.optionTab,
              token ? token.symbol : ''
            )
          }}
          currency={token}
        />
      </div>
      <BalanceContainer visible={showBalance}>
        <SimpleBalance
          balance={balance}
          decimals={token.decimals}
          loading={loadingBalance}
          exceed={props.amount ? Number(props.amount) > Number(balance) : false}
        />

        {showMax && (
          <MaxButton
            onMaxClick={() => {
              const amount = onAmountChange(balance)
              if (inputRef.current && token && amount) {
                inputRef.current.value = fromWei(
                  amount,
                  token.decimals
                ).toString()
              }
            }}
          />
        )}
      </BalanceContainer>
      <SwapCurrencySelectorPanel
        onExit={() => setShowSelector(false)}
        validateCurrency={enableToken}
        selectCurrency={newT => selectCurrency(newT)}
        showCurrencySelector={showSelector}
        currencies={tokens}
      />
    </SwapInputPanel>
  )
}

const BalanceContainer = styled.div<{ visible: boolean }>`
  justify-content: flex-end;
  display: flex;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`
